import { useMemo } from 'react';
import {
  getNavForRoleAndModules,
  isCustomerRole,
  type NavItem,
} from '../config/navConfig';

/**
 * A single bottom-nav destination for the pillar-aware (customer-tier) mobile
 * nav. Mirrors the fields the bottom bar actually renders.
 */
export interface MobileNavItem {
  path: string;
  label: string;
  icon: string;
}

export interface MobileNavLayout {
  /** First N items shown directly in the bar (incl. the leading Home item). */
  primary: MobileNavItem[];
  /** Items that overflow into the "More" sheet. */
  overflow: MobileNavItem[];
}

/** The leading "Home" tab — the operator mobile dashboard. */
const HOME_ITEM: MobileNavItem = { path: '/dashboard', label: 'Home', icon: '🏠' };

/**
 * Derives the pillar-aware mobile bottom-nav layout for a CUSTOMER-TIER user,
 * from the same role/module/pillar gating the sidebar uses
 * (getNavForRoleAndModules). The result is tenant-shaped: a giving+events tenant
 * yields Giving + Events tabs, an events-only tenant yields no Giving tab, etc.
 *
 * Layout: Home + up to 3 pillar tabs shown directly (4 real slots, with a 5th
 * "More" slot added by the bar itself), everything else overflows to the sheet.
 *
 * NOTE: This hook is ONLY meaningful for customer-tier roles. Legacy KDU /
 * attendee roles keep their existing hardcoded reunion nav and never call this.
 *
 * @param role            the current user's role
 * @param enabledModules  modules enabled for the SELECTED event (or null)
 * @param tenantModules   tenant-level pillar modules (e.g. 'giving')
 * @param maxPrimary      max tabs shown directly INCLUDING Home (default 4; the
 *                        bar reserves a 5th slot for the "More" button)
 */
export function useMobileNavItems(
  role: string | undefined | null,
  enabledModules: string[] | null,
  tenantModules: string[],
  maxPrimary = 4,
): MobileNavLayout {
  return useMemo(() => {
    if (!isCustomerRole(role || '')) {
      // Not a customer-tier role: no pillar-aware layout. Callers gate on this.
      return { primary: [], overflow: [] };
    }

    // Flatten the gated nav groups into a single ordered destination list.
    const groups = getNavForRoleAndModules(role || '', enabledModules, tenantModules);
    const flat: MobileNavItem[] = [];
    const seen = new Set<string>([HOME_ITEM.path]);
    for (const group of groups) {
      for (const item of group.items as NavItem[]) {
        // Dashboard IS the Home tab — don't duplicate it in the pillar list.
        if (item.path === HOME_ITEM.path) continue;
        if (seen.has(item.path)) continue;
        seen.add(item.path);
        flat.push({ path: item.path, label: item.label, icon: item.icon });
      }
    }

    // Home always leads; then as many pillar tabs as fit before "More".
    const pillarSlots = Math.max(0, maxPrimary - 1); // Home takes one slot
    const primary: MobileNavItem[] = [HOME_ITEM, ...flat.slice(0, pillarSlots)];
    const overflow: MobileNavItem[] = flat.slice(pillarSlots);

    return { primary, overflow };
  }, [role, enabledModules, tenantModules, maxPrimary]);
}
